import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight, MapPin, Storefront, ArrowUpRight, CircleDashed } from "@phosphor-icons/react";
import client from "../lib/api";


const TIER_STYLE = {
  strong:   { bg: "bg-emerald-500/12", border: "border-emerald-400/40", text: "text-emerald-400", label: "STRONG" },
  good:     { bg: "bg-[#FF5A1F]/12",   border: "border-[#FF5A1F]/40",   text: "text-[#FF5A1F]",   label: "GOOD"   },
};

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmtAge = (mins) => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

/**
 * Dashboard widget: "🔥 Loads worth your time right now"
 * Fetches top-3 STRONG/GOOD picks across all user's saved-lane searches.
 * Hidden entirely when the user has no saved lanes (avoids empty-state clutter).
 */
export default function TopPicksCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/loadboard/top-picks")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ picks: [], saved_lanes_count: 0 }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Hide the whole card if user hasn't saved any lanes yet (no signal to show)
  if (loading) return null;
  if (!data || data.saved_lanes_count === 0) return null;

  return (
    <div className="card-surface p-6" data-testid="top-picks-card">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">
            <Sparkle size={13} weight="fill" />
            LOADS WORTH YOUR TIME
          </div>
          <h3 className="font-display text-xl font-semibold text-white">
            {data.picks.length > 0
              ? `${data.picks.length} STRONG pick${data.picks.length === 1 ? "" : "s"} from your saved lanes`
              : "No fresh picks right now"}
          </h3>
        </div>
        <Link
          to="/app/loadboard"
          className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1 shrink-0"
          data-testid="top-picks-view-all"
        >
          Load Board <ArrowRight size={11} />
        </Link>
      </div>

      {data.picks.length === 0 ? (
        <div className="p-5 border border-dashed border-white/10 rounded-lg text-center" data-testid="top-picks-empty">
          <CircleDashed size={24} className="text-neutral-600 mx-auto mb-2" />
          <div className="text-xs text-neutral-500">
            Scanned {data.saved_lanes_count} saved lane{data.saved_lanes_count === 1 ? "" : "s"} — no STRONG matches right now.
            Try loosening your $/mile floor on the Load Board.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="top-picks-list">
          {data.picks.map((pick) => (
            <PickRow key={pick.match_id} pick={pick} />
          ))}
        </div>
      )}

      {data.provider_live === false && data.picks.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-1.5 text-[10px] text-amber-400/70">
          <Storefront size={11} />
          Demo mode — real DAT + Truckstop feeds go live post partner approval.
        </div>
      )}
    </div>
  );
}


function PickRow({ pick }) {
  const tier = TIER_STYLE[pick.ai_tier] || TIER_STYLE.good;
  return (
    <Link
      to="/app/loadboard"
      className={`block p-3 rounded-md bg-white/[0.015] border ${tier.border} hover:bg-white/[0.03] transition-colors`}
      data-testid={`top-pick-${pick.match_id.slice(0, 8)}`}
    >
      <div className="flex items-center gap-3">
        {/* Score badge */}
        <div className={`shrink-0 w-14 rounded-md ${tier.bg} border ${tier.border} py-1.5 text-center`}>
          <div className={`font-display text-lg font-semibold leading-none ${tier.text}`}>
            {Math.round(pick.ai_score)}
          </div>
          <div className={`text-[8px] tracking-widest ${tier.text} mt-1`}>{tier.label}</div>
        </div>

        {/* Lane */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-white text-sm font-medium truncate">
            <MapPin size={12} className="text-neutral-500 shrink-0" />
            {pick.origin_city}, {pick.origin_state}
            <ArrowRight size={11} className="text-neutral-600 mx-0.5" />
            {pick.destination_city}, {pick.destination_state}
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
            {pick.miles?.toLocaleString()}mi · {pick.deadhead_miles > 0 ? `${pick.deadhead_miles}mi DH` : "no DH"} · {pick.broker_company} · {fmtAge(pick.age_min)} ago
          </div>
        </div>

        {/* Money */}
        <div className="shrink-0 text-right">
          <div className="text-sm font-mono text-white tabular-nums">{fmtMoney(pick.posted_rate)}</div>
          <div className={`text-xs font-mono tabular-nums ${tier.text}`}>ask {fmtMoney(pick.ai_target_rate)}</div>
        </div>

        <ArrowUpRight size={14} className="text-neutral-500 shrink-0" />
      </div>
    </Link>
  );
}
